// src/components/MyPage/MyBookmarks.jsx
import { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardHeader, Avatar,
  Alert, Pagination, Stack, useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import dayjs from 'dayjs';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import SafeChip from '../Common/SafeChip';
import { useTheme } from '@mui/material/styles';

const PAGE_SIZE = 10;

const MyBookmarks = () => {
  const [bookmarks, setBookmarks] = useState([]);
  const [page, setPage] = useState(1);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    api.get('/api/interactions/my-bookmarks/')
      .then(res => setBookmarks(res.data.bookmarks || []))
      .catch(() => setError('북마크한 글을 불러오지 못했습니다.'));
  }, []);

  const pageCount = Math.ceil(bookmarks.length / PAGE_SIZE);
  const slice = bookmarks.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (bookmarks.length === 0)
    return <Typography align="center" sx={{ mt: 4 }}>🔖 북마크한 게시글이 없습니다.</Typography>;

  const titleLimit = isMobile ? 5 : 15;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h6" fontWeight="bold" mb={2}>
        🔖 북마크한 게시글
      </Typography>

      {slice.map((post) => {
        /* 제목 변환 */
        const fullTitle = post.title || '(제목 없음)';
        const shortTitle = fullTitle.length > titleLimit
          ? fullTitle.slice(0, titleLimit) + '…'
          : fullTitle;

        /* 작성자 표시 – 익명 게시판이면 항상 '익명' */
        const displayAuthor = post.is_anonymous
          ? '익명'
          : (post.nickname || post.actual_author || '알 수 없음');

        return (
          <Card
            key={post.id}
            variant="outlined"
            sx={{
              mb: 2, pt: 1, pb: 1,
              borderWidth: 2, borderColor: '#ff9800',
              cursor: 'pointer',
              '&:hover': { boxShadow: 4, transform: 'translateY(-2px)', transition: '.2s' },
            }}
            onClick={() => navigate(`/main/posts/${post.id}`)}
          >
            <CardHeader
              avatar={
                <Avatar sx={{ bgcolor: '#ff9800', color: '#fff' }}>
                  <BookmarkIcon />
                </Avatar>
              }
              title={
                <Stack
                  direction={isMobile ? 'column' : 'row'}
                  alignItems={isMobile ? 'flex-start' : 'center'}
                  spacing={1}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    title={fullTitle}
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: isMobile ? '100%' : 400,
                      textAlign: 'left',
                    }}
                  >
                    {shortTitle}
                  </Typography>

                  <Stack direction="row" spacing={1}>
                    {post.is_anonymous && <SafeChip label="익명글" color="default" size="small" />}
                    {post.is_secret && <SafeChip label="비밀글" color="error" size="small" />}
                  </Stack>
                </Stack>
              }
              subheader={`작성자: ${displayAuthor} · 작성일: ${dayjs(post.created_at).format('YYYY-MM-DD')}`}
              subheaderTypographyProps={{ fontSize: 12 }}
              sx={{ p: 0, pl: 2, pr: 2 }}
            />
          </Card>
        );
      })}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={pageCount}
            page={page}
            onChange={(_, v) => setPage(v)}
            showFirstButton
            showLastButton
            siblingCount={1}
            size="small"
          />
        </Box>
      )}
    </Box>
  );
};

export default MyBookmarks;
